// src/templates/chat-template.tsx
import { cn, Density } from "@modootoday/ui-primitives";
import { jsx, jsxs } from "react/jsx-runtime";
function ChatTemplate({
  sessions,
  sessionTitle,
  headerActions,
  children,
  composer,
  inspector,
  emptyState,
  className
}) {
  return /* @__PURE__ */ jsxs(
    Density,
    {
      level: "reading",
      className: cn("flex h-full min-h-0 w-full", className),
      children: [
        sessions && /* @__PURE__ */ jsx("aside", { className: "hidden w-64 shrink-0 border-r border-stroke-subtle lg:block", children: sessions }),
        /* @__PURE__ */ jsxs("main", { className: "flex min-h-0 min-w-0 flex-1 flex-col", children: [
          (sessionTitle || headerActions) && /* @__PURE__ */ jsxs("header", { className: "flex h-11 items-center justify-between border-b border-stroke-subtle px-4 text-sm", children: [
            /* @__PURE__ */ jsx("div", { className: "min-w-0 flex-1 truncate text-text-primary", children: sessionTitle }),
            /* @__PURE__ */ jsx("div", { className: "flex items-center gap-2", children: headerActions })
          ] }),
          /* @__PURE__ */ jsx("div", { className: "flex-1 overflow-y-auto", children: /* @__PURE__ */ jsx("div", { className: "mx-auto flex max-w-3xl flex-col gap-4 px-4 py-6", children: children ?? emptyState }) }),
          /* @__PURE__ */ jsx("div", { className: "border-t border-stroke-subtle bg-surface-base px-4 py-4", children: /* @__PURE__ */ jsx("div", { className: "mx-auto max-w-3xl", children: composer }) })
        ] }),
        inspector && /* @__PURE__ */ jsx("aside", { className: "hidden w-[var(--inspector-width,360px)] shrink-0 border-l border-stroke-subtle xl:block", children: inspector })
      ]
    }
  );
}

// src/templates/analysis-doc-template.tsx
import { cn as cn2, Density as Density2 } from "@modootoday/ui-primitives";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
function AnalysisDocTemplate({
  header,
  toc,
  children,
  inspector,
  inspectorTitle,
  outline,
  className
}) {
  return /* @__PURE__ */ jsxs2(
    Density2,
    {
      level: "reading",
      className: cn2("flex h-full w-full min-h-0", className),
      children: [
        outline && /* @__PURE__ */ jsx2("aside", { className: "hidden w-56 shrink-0 border-r border-stroke-subtle px-3 py-4 md:block", children: outline }),
        /* @__PURE__ */ jsxs2("div", { className: "flex min-w-0 flex-1 flex-col", children: [
          /* @__PURE__ */ jsx2("div", { className: "sticky top-0 z-10 border-b border-stroke-subtle bg-surface-base/95 px-6 py-3 backdrop-blur", children: header }),
          /* @__PURE__ */ jsxs2("div", { className: "flex min-h-0 flex-1", children: [
            /* @__PURE__ */ jsx2("article", { className: "mx-auto flex max-w-3xl flex-1 flex-col gap-4 px-6 py-6 text-text-primary", children }),
            toc && toc.length > 0 && /* @__PURE__ */ jsxs2(
              "nav",
              {
                className: "hidden w-48 shrink-0 overflow-y-auto px-3 py-6 text-xs text-text-tertiary lg:block",
                "aria-label": "On-page navigation",
                children: [
                  /* @__PURE__ */ jsx2("p", { className: "mb-2 font-medium text-text-secondary", children: "\uBAA9\uCC28" }),
                  /* @__PURE__ */ jsx2("ol", { className: "flex flex-col gap-1", children: toc.map((t) => /* @__PURE__ */ jsx2("li", { className: t.level === 2 ? "pl-3" : "", children: /* @__PURE__ */ jsx2("a", { href: `#${t.id}`, className: "hover:text-text-brand", children: t.label }) }, t.id)) })
                ]
              }
            )
          ] })
        ] }),
        inspector && /* @__PURE__ */ jsxs2("aside", { className: "hidden w-[var(--inspector-width,360px)] shrink-0 border-l border-stroke-subtle xl:block", children: [
          inspectorTitle && /* @__PURE__ */ jsx2("div", { className: "border-b border-stroke-subtle px-4 py-3 text-sm font-medium text-text-primary", children: inspectorTitle }),
          /* @__PURE__ */ jsx2("div", { className: "overflow-y-auto p-4", children: inspector })
        ] })
      ]
    }
  );
}

// src/templates/console-template.tsx
import { cn as cn3, Density as Density3 } from "@modootoday/ui-primitives";
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
function ConsoleTemplate({
  toolbar,
  nav,
  children,
  inspector,
  inspectorTitle,
  footer,
  className
}) {
  return /* @__PURE__ */ jsxs3(
    Density3,
    {
      level: "console",
      className: cn3("flex h-full w-full min-h-0 flex-col", className),
      children: [
        toolbar && /* @__PURE__ */ jsx3("div", { className: "flex h-12 shrink-0 items-center border-b border-stroke-subtle px-4", children: toolbar }),
        /* @__PURE__ */ jsxs3("div", { className: "flex min-h-0 flex-1", children: [
          nav && /* @__PURE__ */ jsx3("aside", { className: "hidden w-56 shrink-0 border-r border-stroke-subtle p-3 md:block", children: nav }),
          /* @__PURE__ */ jsx3("main", { className: "min-w-0 flex-1 overflow-y-auto p-4", children }),
          inspector && /* @__PURE__ */ jsxs3("aside", { className: "hidden w-[var(--inspector-width,360px)] shrink-0 border-l border-stroke-subtle xl:flex xl:flex-col", children: [
            inspectorTitle && /* @__PURE__ */ jsx3("div", { className: "flex h-10 items-center border-b border-stroke-subtle px-4 text-sm font-medium text-text-primary", children: inspectorTitle }),
            /* @__PURE__ */ jsx3("div", { className: "flex-1 overflow-y-auto p-4", children: inspector })
          ] })
        ] }),
        footer && /* @__PURE__ */ jsx3("div", { className: "flex h-14 shrink-0 items-center justify-end gap-2 border-t border-stroke-subtle bg-surface-base px-4", children: footer })
      ]
    }
  );
}

// src/templates/dashboard-template.tsx
import { cn as cn4, Density as Density4 } from "@modootoday/ui-primitives";
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
function DashboardTemplate({
  toolbar,
  children,
  maxWidth = 1440,
  className
}) {
  return /* @__PURE__ */ jsxs4(
    Density4,
    {
      level: "console",
      className: cn4("flex h-full w-full min-h-0 flex-col", className),
      children: [
        toolbar && /* @__PURE__ */ jsx4("div", { className: "sticky top-0 z-10 flex h-12 items-center gap-2 border-b border-stroke-subtle bg-surface-base/95 px-6 backdrop-blur", children: toolbar }),
        /* @__PURE__ */ jsx4("main", { className: "flex-1 overflow-y-auto", children: /* @__PURE__ */ jsx4(
          "div",
          {
            className: "mx-auto px-6 py-6",
            style: { maxWidth: `${maxWidth}px` },
            children
          }
        ) })
      ]
    }
  );
}

// src/templates/dashboard-viewport-template.tsx
import { cn as cn5, Density as Density5 } from "@modootoday/ui-primitives";
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
function DashboardViewportTemplate({
  toolbar,
  header,
  children,
  density = "console",
  className,
  mainClassName
}) {
  return /* @__PURE__ */ jsxs5(
    Density5,
    {
      level: density,
      className: cn5(
        "@container flex h-full w-full min-h-0 flex-col overflow-hidden bg-surface-base",
        className
      ),
      children: [
        toolbar && /* @__PURE__ */ jsx5("div", { className: "sticky top-0 z-10 flex h-12 shrink-0 items-center gap-2 border-b border-stroke-subtle bg-surface-base/95 px-4 backdrop-blur md:px-6", children: toolbar }),
        header && /* @__PURE__ */ jsx5("div", { className: "shrink-0", children: header }),
        /* @__PURE__ */ jsx5("main", { className: cn5("flex-1 min-h-0", mainClassName), children })
      ]
    }
  );
}

// src/templates/data-grid-template.tsx
import { cn as cn6, Density as Density6 } from "@modootoday/ui-primitives";
import { jsx as jsx6, jsxs as jsxs6 } from "react/jsx-runtime";
function DataGridTemplate({
  toolbar,
  formulaBar,
  children,
  inspector,
  statusBar,
  className
}) {
  return /* @__PURE__ */ jsxs6(
    Density6,
    {
      level: "grid",
      className: cn6("flex h-full w-full min-h-0 flex-col", className),
      children: [
        toolbar && /* @__PURE__ */ jsx6("div", { className: "flex h-10 shrink-0 items-center gap-1 border-b border-stroke-subtle px-3", children: toolbar }),
        formulaBar && /* @__PURE__ */ jsx6("div", { className: "flex h-9 shrink-0 items-center border-b border-stroke-subtle bg-surface-sunken px-3 font-mono text-xs text-text-secondary", children: formulaBar }),
        /* @__PURE__ */ jsxs6("div", { className: "flex min-h-0 flex-1", children: [
          /* @__PURE__ */ jsx6("div", { className: "min-w-0 flex-1 overflow-auto", children }),
          inspector && /* @__PURE__ */ jsx6("aside", { className: "hidden w-[var(--inspector-width,360px)] shrink-0 border-l border-stroke-subtle lg:block", children: /* @__PURE__ */ jsx6("div", { className: "h-full overflow-y-auto", children: inspector }) })
        ] }),
        statusBar && /* @__PURE__ */ jsx6("div", { className: "flex h-7 shrink-0 items-center justify-between border-t border-stroke-subtle bg-surface-sunken px-3 text-xs text-text-tertiary", children: statusBar })
      ]
    }
  );
}

// src/templates/marketing-template.tsx
import { cn as cn7, Density as Density7 } from "@modootoday/ui-primitives";
import { jsx as jsx7, jsxs as jsxs7 } from "react/jsx-runtime";
function MarketingTemplate({
  nav,
  children,
  footer,
  maxWidth = 1200,
  className
}) {
  return /* @__PURE__ */ jsxs7(
    Density7,
    {
      level: "reading",
      className: cn7(
        "flex min-h-screen flex-col bg-surface-base text-text-primary",
        className
      ),
      children: [
        /* @__PURE__ */ jsx7("header", { className: "sticky top-0 z-10 border-b border-stroke-subtle bg-surface-base/80 backdrop-blur", children: /* @__PURE__ */ jsx7(
          "div",
          {
            className: "mx-auto flex h-16 items-center px-6",
            style: { maxWidth: `${maxWidth}px` },
            children: nav
          }
        ) }),
        /* @__PURE__ */ jsx7("main", { className: "flex-1", children }),
        /* @__PURE__ */ jsx7("footer", { className: "border-t border-stroke-subtle bg-surface-base", children: /* @__PURE__ */ jsx7(
          "div",
          {
            className: "mx-auto px-6 py-12",
            style: { maxWidth: `${maxWidth}px` },
            children: footer
          }
        ) })
      ]
    }
  );
}

// src/templates/auth-template.tsx
import { cn as cn8, Density as Density8 } from "@modootoday/ui-primitives";
import { jsx as jsx8, jsxs as jsxs8 } from "react/jsx-runtime";
function AuthTemplate({
  title,
  subtitle,
  children,
  footer,
  logo,
  standalone = false,
  className
}) {
  const card = /* @__PURE__ */ jsxs8("div", { className: "w-full max-w-[400px] rounded-sm border border-stroke-subtle bg-surface-raised p-6", children: [
    /* @__PURE__ */ jsx8("h1", { className: "text-lg font-semibold text-text-primary", children: title }),
    subtitle && /* @__PURE__ */ jsx8("p", { className: "mt-1 text-sm text-text-secondary", children: subtitle }),
    /* @__PURE__ */ jsx8("div", { className: "mt-6 flex flex-col gap-3", children })
  ] });
  if (!standalone && !logo && !footer) {
    return /* @__PURE__ */ jsx8(Density8, { level: "reading", className: cn8("w-full", className), children: card });
  }
  return /* @__PURE__ */ jsxs8(
    Density8,
    {
      level: "reading",
      className: cn8(
        "flex min-h-screen flex-col bg-surface-base text-text-primary",
        className
      ),
      children: [
        logo && /* @__PURE__ */ jsx8("header", { className: "flex h-14 items-center justify-center border-b border-stroke-subtle", children: logo }),
        /* @__PURE__ */ jsx8("main", { className: "flex flex-1 items-center justify-center px-4 py-10", children: card }),
        footer && /* @__PURE__ */ jsx8("footer", { className: "border-t border-stroke-subtle py-4 text-center text-xs text-text-tertiary", children: footer })
      ]
    }
  );
}

export {
  ChatTemplate,
  AnalysisDocTemplate,
  ConsoleTemplate,
  DashboardTemplate,
  DashboardViewportTemplate,
  DataGridTemplate,
  MarketingTemplate,
  AuthTemplate
};
//# sourceMappingURL=chunk-YB2DVWGF.js.map